/**
 * Menu « Applications » : bascule vers les autres apps de l'écosystème
 * (synotis-support#23). Même logique que le menu de Synolog (copie, pas de
 * lib commune).
 *
 * - Liste déclarative ; URL par variable `VITE_*_URL` (staging ≠ prod, posée
 *   par la CI). Synoplace, Synobatch et Assets sont hors périmètre.
 * - Visibilité : un admin voit tout ; sinon une app n'est masquée que si sa
 *   route `/api/auth/access` (lecture seule, jamais `/auth/me` qui crée le
 *   compte) refuse explicitement l'accès. Vérification en cours ou en échec :
 *   l'app reste affichée, l'app cible applique de toute façon ses droits.
 * - Ouverture dans un onglet nommé par app : un second clic le ramène au
 *   premier plan sans le recharger (la session de l'onglet est conservée).
 */
const SYNOLOG_URL = import.meta.env?.VITE_SYNOLOG_URL || 'https://synolog.synotis.tech';
const SYNOSIGHT_URL = globalThis.window?.location?.origin || 'https://synosight.synotis.tech';

export const ECOSYSTEM_APPS = [
  { key: 'synolog', label: 'Synolog', url: SYNOLOG_URL, accessUrl: `${SYNOLOG_URL}/api/auth/access` },
  { key: 'synosight', label: 'Synosight', url: SYNOSIGHT_URL, current: true },
];

/** Nom de fenêtre stable : c'est lui qui permet de retrouver l'onglet. */
export function appWindowName(app) {
  return `synotis-${app.key}`;
}

/** `access` : `{ [key]: boolean }`, clé absente = accès inconnu. */
export function visibleApps(apps, { isAdmin, access = {} } = {}) {
  return apps.filter((app) => app.current || isAdmin || access[app.key] !== false);
}

/** Interroge la route d'accès de chaque autre app (cookie SSO partagé). */
export async function fetchAppAccess(apps, fetchImpl = globalThis.fetch) {
  const out = {};
  await Promise.all(
    apps
      .filter((app) => !app.current && app.accessUrl)
      .map(async (app) => {
        try {
          const res = await fetchImpl(app.accessUrl, { credentials: 'include' });
          if (!res.ok) return;
          const body = await res.json();
          if (typeof body?.hasAccess === 'boolean') out[app.key] = body.hasAccess;
        } catch {
          // CORS, réseau, app arrêtée : accès inconnu.
        }
      }),
  );
  return out;
}

/**
 * Ouvre l'app ou ramène son onglet au premier plan. À n'appeler que dans un
 * gestionnaire de clic (bloqueurs de popups).
 *
 * `open('', nom)` retrouve un onglet existant sans le naviguer ; un onglet
 * neuf est vide (`about:blank`, même origine) et reçoit alors l'URL. Un
 * onglet déjà chargé est d'une autre origine : lire son `location` lève, on
 * ne fait que le focaliser. Pas de `noopener` : il empêcherait de retrouver
 * l'onglet par son nom (les deux apps sont internes, même domaine parent).
 */
export function openAppTab(app, win = globalThis.window) {
  const tab = win?.open('', appWindowName(app));
  if (!tab) return false;
  let fresh = false;
  try {
    fresh = tab.location.href === 'about:blank';
  } catch {
    fresh = false;
  }
  if (fresh) tab.location.href = app.url;
  tab.focus?.();
  return true;
}
